import type {
  Action,
  BettingState,
  BlindLevel,
  HandState,
  LegalAction,
  Player,
  PlayerId,
  Seat,
  Street,
} from './types';

const MAX_SEATS = 6;

/** Look up a specific legal action by type. Used by UI (ActionBar,
 *  keyboard shortcuts) and GTO (decidePostflop) to avoid repeating the
 *  same typed `.find` pattern. */
export function findLegal<T extends LegalAction['type']>(
  legals: ReadonlyArray<LegalAction>,
  type: T,
): (LegalAction & { type: T }) | undefined {
  return legals.find((l): l is LegalAction & { type: T } => l.type === type);
}

function findPlayer(state: HandState, playerId: PlayerId): Player | undefined {
  return state.players.find((p) => p.id === playerId);
}

function activePlayers(state: HandState): Player[] {
  return state.players.filter((p) => !p.folded && !p.eliminated);
}

/**
 * Active players who can still voluntarily bet (have chips and are not folded or all-in).
 * Drives pendingActor computation and street termination.
 */
export function actingPlayers(state: HandState): Player[] {
  return state.players.filter((p) => !p.folded && !p.eliminated && !p.allIn && p.stack > 0);
}

/** Is the hand down to a single non-folded player (pot wins uncontested). */
export function isHandOver(state: HandState): boolean {
  return activePlayers(state).length <= 1;
}

/** Highest seat index currently in play. Used for button advancement. */
export function nextSeatClockwise(startSeat: Seat, seats: ReadonlyArray<Seat>): Seat {
  if (seats.length === 0) {
    throw new RangeError('nextSeatClockwise: no seats provided');
  }
  for (let offset = 1; offset <= MAX_SEATS; offset++) {
    const candidate = ((startSeat + offset) % MAX_SEATS) as Seat;
    if (seats.includes(candidate)) return candidate;
  }
  throw new Error('nextSeatClockwise: unable to find next seat');
}

/** Compute the chip amount needed to match the current bet. 0 if no action. */
export function amountToCall(state: HandState, playerId: PlayerId): number {
  const player = findPlayer(state, playerId);
  if (!player) return 0;
  const toCall = state.betting.currentBet - player.streetContribution;
  return Math.max(0, Math.min(toCall, player.stack));
}

/** Minimum legal raise-to amount given current betting state. */
export function minRaiseTo(state: HandState): number {
  return state.betting.currentBet + state.betting.minRaise;
}

/** Returns the minimum opening bet when no one has wagered on the street yet. */
export function minOpenBet(level: BlindLevel): number {
  return Math.max(1, level.bb);
}

/**
 * Legal actions for the player whose turn it currently is.
 * Returns [] when the player is not the pending actor, has folded, is all-in,
 * or is eliminated.
 */
export function legalActions(state: HandState, playerId: PlayerId): LegalAction[] {
  const player = findPlayer(state, playerId);
  if (!player) return [];
  if (state.pendingActor !== playerId) return [];
  if (player.folded || player.allIn || player.eliminated || player.stack === 0) return [];

  const toCall = state.betting.currentBet - player.streetContribution;
  const actions: LegalAction[] = [];

  if (toCall <= 0) {
    actions.push({ type: 'check' });
    const minBet = minOpenBet(state.level);
    if (player.stack >= minBet) {
      actions.push({ type: 'bet', min: minBet, max: player.stack });
    }
  } else {
    actions.push({ type: 'fold' });
    const callAmount = Math.min(toCall, player.stack);
    actions.push({ type: 'call', amount: callAmount });
    const maxRaiseTo = player.streetContribution + player.stack;
    const minRaise = minRaiseTo(state);
    if (maxRaiseTo >= minRaise) {
      actions.push({ type: 'raise', min: minRaise, max: maxRaiseTo });
    }
  }

  if (player.stack > 0) {
    actions.push({
      type: 'allin',
      amount: player.streetContribution + player.stack,
    });
  }

  return actions;
}

/**
 * A raise reopens action (allows previously-acted players to raise again)
 * only when the raise amount is at least a full minimum raise.
 * Partial all-in raises below the min are "short all-ins" that do NOT reopen.
 */
export function reopensAction(state: HandState, raiseTo: number): boolean {
  const raiseBy = raiseTo - state.betting.currentBet;
  return raiseBy >= state.betting.minRaise;
}

/**
 * Heads-up action order flip (§4.1 L191):
 * - Preflop: SB (also button) acts first, BB acts last.
 * - Postflop: BB acts first, SB (button) acts last.
 * Returns true when the given street uses the flipped order for heads-up play.
 */
export function isHeadsUp(state: HandState): boolean {
  return activePlayers(state).length === 2;
}

/**
 * True iff the hand was *dealt* heads-up (exactly 2 non-eliminated seats at
 * the start of this hand). Distinct from `isHeadsUp`, which only checks the
 * *current* active count — that is fooled by a multi-way hand where folds
 * leave two survivors. The preflop→postflop action-order flip (§4.1 L191)
 * is only real when the hand started heads-up; for a 6-way hand that
 * happens to fold down to two, action continues under the regular rule
 * (first live seat clockwise from SB), so callers choosing postflop
 * first-actors must gate on `startedHeadsUp`, not `isHeadsUp`.
 */
export function startedHeadsUp(state: HandState): boolean {
  return state.players.filter((p) => !p.eliminated).length === 2;
}

export function firstToActSeatHeadsUp(state: HandState, street: Street): Seat {
  if (!isHeadsUp(state)) {
    throw new Error('firstToActSeatHeadsUp: not a heads-up state');
  }
  const alive = activePlayers(state).map((p) => p.seat);
  if (alive.length !== 2) {
    throw new Error('firstToActSeatHeadsUp: need exactly 2 active seats');
  }
  const buttonSeat = state.button;
  const nonButtonSeat = alive.find((s) => s !== buttonSeat);
  if (nonButtonSeat === undefined) {
    throw new Error('firstToActSeatHeadsUp: button seat not found among actives');
  }
  return street === 'preflop' ? buttonSeat : nonButtonSeat;
}

/**
 * Verify a proposed action is currently legal. Used by the reducer to decide
 * whether to throw IllegalActionError.
 */
export function isActionLegal(
  state: HandState,
  action: Action,
): { ok: true } | { ok: false; reason: string } {
  const legals = legalActions(state, action.playerId);
  if (legals.length === 0) {
    return { ok: false, reason: 'no legal actions for player' };
  }

  const player = findPlayer(state, action.playerId);
  if (!player) return { ok: false, reason: 'unknown player' };

  switch (action.type) {
    case 'fold':
      return legals.some((l) => l.type === 'fold')
        ? { ok: true }
        : { ok: false, reason: 'fold not legal' };
    case 'check':
      return legals.some((l) => l.type === 'check')
        ? { ok: true }
        : { ok: false, reason: 'check not legal' };
    case 'call': {
      const legal = legals.find((l) => l.type === 'call');
      if (!legal) return { ok: false, reason: 'call not legal' };
      if (legal.amount !== action.amount) {
        return { ok: false, reason: `call amount ${action.amount} !== required ${legal.amount}` };
      }
      return { ok: true };
    }
    case 'bet': {
      const legal = legals.find((l) => l.type === 'bet');
      if (!legal) return { ok: false, reason: 'bet not legal' };
      if (legal.min === undefined || legal.max === undefined) {
        return { ok: false, reason: 'bet bounds missing' };
      }
      if (action.amount < legal.min || action.amount > legal.max) {
        return {
          ok: false,
          reason: `bet amount ${action.amount} outside [${legal.min}, ${legal.max}]`,
        };
      }
      return { ok: true };
    }
    case 'raise': {
      const legal = legals.find((l) => l.type === 'raise');
      if (!legal) return { ok: false, reason: 'raise not legal' };
      if (legal.min === undefined || legal.max === undefined) {
        return { ok: false, reason: 'raise bounds missing' };
      }
      if (action.to < legal.min || action.to > legal.max) {
        return {
          ok: false,
          reason: `raise-to ${action.to} outside [${legal.min}, ${legal.max}]`,
        };
      }
      return { ok: true };
    }
    case 'allin': {
      const legal = legals.find((l) => l.type === 'allin');
      if (!legal) return { ok: false, reason: 'allin not legal' };
      const expected = player.streetContribution + player.stack;
      if (action.amount !== expected) {
        return { ok: false, reason: `allin amount ${action.amount} !== stack ${expected}` };
      }
      return { ok: true };
    }
  }
}

/**
 * Showdown reveal order per §4.1 L191: the last aggressor shows first;
 * remaining non-folded seats follow in clockwise order starting after that
 * aggressor. If no aggression happened on the final betting round (e.g., a
 * check-through river), fall back to first-to-act clockwise from the SB
 * (equivalently: first live seat clockwise from the button).
 *
 * Returns [] when no non-folded players exist (degenerate state).
 */
export function showdownRevealOrder(state: HandState): PlayerId[] {
  const contenders = state.players.filter((p) => !p.folded && !p.eliminated);
  if (contenders.length === 0) return [];
  const contenderSeats = contenders.map((p) => p.seat).sort((a, b) => a - b);
  const lastAggressor = state.betting.lastAggressor;
  let startSeat: Seat;
  if (lastAggressor) {
    const aggressor = state.players.find((p) => p.id === lastAggressor);
    if (aggressor && contenderSeats.includes(aggressor.seat)) {
      startSeat = aggressor.seat;
    } else {
      startSeat = nextSeatClockwise(state.button, contenderSeats);
    }
  } else {
    startSeat = nextSeatClockwise(state.button, contenderSeats);
  }
  const ordered: PlayerId[] = [];
  let seat = startSeat;
  for (let i = 0; i < contenders.length; i++) {
    const player = contenders.find((p) => p.seat === seat);
    if (player) ordered.push(player.id);
    if (i < contenders.length - 1) {
      seat = nextSeatClockwise(seat, contenderSeats);
    }
  }
  return ordered;
}

/**
 * Create an empty betting state for the start of a new street.
 * minRaise defaults to BB (first raise must be at least one BB larger than the current bet).
 */
export function emptyBettingState(level: BlindLevel, street: Street): BettingState {
  return {
    street,
    pots: [],
    currentBet: 0,
    lastRaiseSize: 0,
    lastAggressor: null,
    minRaise: minOpenBet(level),
    actedInRound: [],
  };
}
